import { getAmountIncludingVat } from '@features/invoices/utils/getAmountIncludingVat'

import { PaginationProps } from '../../types/paginationProps'
import { SortingProps } from '../../types/sortingProps'
import apiZervant from '../../utils/apiZervant'
import { ProductPricingModel } from './enums/productPricingModel'
import { ProductSortProperty } from './enums/productSortProperty'
import { Product } from './types/product'
import { ProductPayload } from './types/productPayload'

interface FetchProductsParams extends PaginationProps, SortingProps<ProductSortProperty> {
  searchQuery?: string
}

export interface ProductResponse extends Omit<Product, 'id'> {
  id: number
}

/**
 * id converting is needed, because of mismatch between Zervant DB,
 * and what is expected in billy-webapp repository's components,
 * products fetched from Zervant have ids of a number type,
 * while generally in billy-webapp ids are strings,
 * for convenience it was decided to perform the conversion at the very beginning
 */
const normalizeProductResponse = (product: ProductResponse) => ({
  ...product,
  id: String(product.id),
  total: product.total ?? (product.price == null ? undefined : getAmountIncludingVat(product.price, product.vatPct)),
})

export const fetchProducts = async ({
  page,
  pageSize,
  searchQuery,
  sortProperty,
  sortDirection,
}: FetchProductsParams = {}) => {
  const searchParams = new URLSearchParams()

  if (page) {
    searchParams.append('page', String(page))
  }

  if (pageSize && pageSize !== 'all') {
    searchParams.append('size', String(pageSize))
  }

  if (searchQuery) {
    searchParams.append('search', searchQuery)
  }

  if (sortProperty || sortDirection) {
    searchParams.append('sort', [sortProperty, sortDirection?.toLowerCase() ?? ''].filter(Boolean).join(','))
  }

  const response = await apiZervant.platform.get<ProductResponse[]>(`/v4/products?${searchParams.toString()}`)

  return {
    ...response,
    data: response.data.map(normalizeProductResponse),
  }
}

const EXPORT_PAGE_SIZE = 100

export const fetchAllProducts = async (): Promise<Product[]> => {
  const products: Product[] = []

  for (let page = 1; ; page += 1) {
    const { data, headers } = await fetchProducts({ page, pageSize: EXPORT_PAGE_SIZE })
    products.push(...data)

    const totalCount = Number(headers['x-total-count'])
    const reachedKnownTotal = Number.isFinite(totalCount) && products.length >= totalCount

    if (data.length < EXPORT_PAGE_SIZE || reachedKnownTotal) {
      break
    }
  }

  return products
}

export type CreateProductPayload = Omit<Product, 'id' | 'price' | 'total' | 'accountId'> &
  (
    | { pricingModel: ProductPricingModel.Price; price: Product['price'] }
    | { pricingModel: ProductPricingModel.Total; total: Product['total'] }
  ) & { accountId?: string | null }

export const createProduct = async (payload: CreateProductPayload) => {
  const response = await apiZervant.platform.post<ProductResponse>('/v4/products', payload)

  return { ...response, data: normalizeProductResponse(response.data) }
}

export const importProducts = async (payloads: ProductPayload[]) => {
  const response = await apiZervant.platform.post<ProductResponse[]>('/v4/products/import', payloads)

  return { ...response, data: response.data.map(normalizeProductResponse) }
}

type UpdateProductPayload = Omit<Product, 'price' | 'total' | 'accountId'> &
  (
    | { pricingModel: ProductPricingModel.Price; price: Product['price'] }
    | { pricingModel: ProductPricingModel.Total; total: Product['total'] }
  ) & { accountId?: string | null }

export const updateProduct = async (payload: UpdateProductPayload) => {
  const response = await apiZervant.platform.patch<ProductResponse>(`/v4/products/${payload.id}`, payload)
  return { ...response, data: normalizeProductResponse(response.data) }
}

export const deleteProducts = (productIds: string[]) => {
  const URL = `/v4/products?ids=${productIds.join(',')}`
  return apiZervant.platform.delete<void>(URL)
}

export interface UpdateProductsVatProps {
  productIds: string[]
  vatPct: number
  vatRateId: string
}

export const updateProductsVat = ({ productIds, vatPct, vatRateId }: UpdateProductsVatProps) => {
  const URL = `/v4/products?ids=${productIds.join(',')}`
  return apiZervant.platform.patch(URL, { vatPct: String(vatPct), vatRateId })
}

/**
 * A curated accounting account selectable for a product (STR-111). Mirrors the platform's
 * `ProductAccountDTO`, which is serialized with `@JsonInclude(NON_NULL)`, so every field besides
 * `id`/`name` may be omitted from the response.
 */
export interface SelectableProductAccount {
  id: string
  name: string
  accountNo?: number
  publicAccountId?: string
  natureId?: string
  currencyId?: string
  isArchived?: boolean
}

export const fetchSelectableProductAccounts = async (): Promise<SelectableProductAccount[]> => {
  const response = await apiZervant.platform.get<SelectableProductAccount[]>('/v4/products/selectable-accounts')
  return response.data
}
